<script setup lang="ts">
import { BCard, BLink, BSpinner } from 'bootstrap-vue-next';
import { onMounted, ref } from 'vue';
import type { RouteLocationRaw } from 'vue-router';

import type { WeeklyRoundup } from '~/api/weeklyRoundups';
import { fetchLatestWeeklyRoundup } from '~/api/weeklyRoundups';
import ErrorState from '~/common/components/ErrorState.vue';
import UsernameLinks from '~/common/components/UsernameLinks.vue';
import { toLocaleDateStringIgnoringTimezone } from '~/common/DateUtils';

const roundup = ref<WeeklyRoundup | null>(null);
const loading = ref(true);
const error = ref(false);

const formatDateRange = ({ from_date, to_date }: WeeklyRoundup): string => [
  toLocaleDateStringIgnoringTimezone(from_date),
  toLocaleDateStringIgnoringTimezone(to_date),
].join(' – ');

const leaderboardLink = ({ from_date, to_date }: WeeklyRoundup): RouteLocationRaw => ({
  name: 'leaderboard',
  query: {
    fromDate: from_date,
    toDate: to_date,
  },
});

onMounted(async () => {
  try {
    roundup.value = await fetchLatestWeeklyRoundup();
  } catch {
    error.value = true;
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <BCard
    v-if="loading || error || roundup"
    title="Community highlights"
  >
    <BSpinner v-if="loading" />
    <ErrorState v-else-if="error" />
    <template v-else-if="roundup">
      <p class="text-muted">
        Recognizing community contributors for {{ formatDateRange(roundup) }}
      </p>
      <ul class="list-unstyled">
        <li
          v-for="category in roundup.categories"
          :key="category.label"
          class="mb-2"
        >
          {{ category.emoji }} <strong>{{ category.label }}:</strong> <UsernameLinks :usernames="category.usernames" />
        </li>
      </ul>
      <p
        v-if="roundup.additional_usernames.length"
        data-test-id="roundup-additional"
      >
        Also making an impact:
        <UsernameLinks :usernames="roundup.additional_usernames" />
      </p>
      <BLink :to="leaderboardLink(roundup)">View full leaderboard</BLink>
    </template>
  </BCard>
</template>
